// ============================================================
// HoverChat Admin — Knowledge Base Page
// Top-level page for managing KB sources (upload, paste, search).
// Auto-resolves the primary tenant so you don't have to navigate
// through Tenants → Detail → Knowledge tab.
// ============================================================

import { useEffect, useState, useRef } from 'react';
import { api } from '../lib/api';
import {
  Database, Upload, FileText, Search, Trash2, RefreshCw,
  Plus, AlertCircle, CheckCircle2, Clock, Eye
} from 'lucide-react';

export function KnowledgeBasePage() {
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenants, setTenants] = useState<any[]>([]);
  const [sources, setSources] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'sources' | 'upload' | 'paste' | 'search'>('sources');

  // Upload / paste state
  const [uploading, setUploading] = useState(false);
  const [textName, setTextName] = useState('');
  const [textContent, setTextContent] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);

  // Feedback
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Source detail expand
  const [expandedSource, setExpandedSource] = useState<string | null>(null);
  const [sourceChunks, setSourceChunks] = useState<any>(null);

  // Load tenants, pick the oldest one (primary tenant)
  useEffect(() => {
    api.listTenants()
      .then(({ tenants }) => {
        // Sort oldest first so the primary tenant is default
        const sorted = [...tenants].sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
        setTenants(sorted);
        if (sorted.length > 0) {
          setTenantId(sorted[0].id);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Load sources when tenant changes
  useEffect(() => {
    if (tenantId) loadSources();
  }, [tenantId]);

  // Auto-dismiss messages
  useEffect(() => {
    if (message) {
      const t = setTimeout(() => setMessage(null), 6000);
      return () => clearTimeout(t);
    }
  }, [message]);

  const loadSources = async () => {
    if (!tenantId) return;
    try {
      const { sources } = await api.getKBSources(tenantId);
      setSources(sources);
    } catch {}
  };

  // ---- File Upload ----
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !tenantId) return;
    setUploading(true);
    setMessage(null);
    try {
      const result = await api.uploadKBFile(tenantId, file);
      setMessage({
        text: `Uploaded "${file.name}" — ${result.chunk_count} chunks created from ${(result.text_length / 1024).toFixed(1)} KB of text`,
        type: 'success',
      });
      loadSources();
      setActiveTab('sources');
    } catch (err: any) {
      setMessage({ text: err.message, type: 'error' });
    }
    setUploading(false);
    e.target.value = '';
  };

  // ---- Paste Text ----
  const handleTextSubmit = async () => {
    if (!textName.trim() || !textContent.trim() || !tenantId) return;
    setUploading(true);
    setMessage(null);
    try {
      const result = await api.addKBText(tenantId, { name: textName, content: textContent });
      setMessage({ text: `Added "${textName}" — ${result.chunk_count} chunks created`, type: 'success' });
      setTextName('');
      setTextContent('');
      loadSources();
      setActiveTab('sources');
    } catch (err: any) {
      setMessage({ text: err.message, type: 'error' });
    }
    setUploading(false);
  };

  // ---- Delete ----
  const handleDelete = async (sourceId: string, name: string) => {
    if (!confirm(`Delete "${name}" and all its chunks?`)) return;
    if (!tenantId) return;
    try {
      await api.deleteKBSource(tenantId, sourceId);
      setSources(sources.filter(s => s.id !== sourceId));
      setMessage({ text: `Deleted "${name}"`, type: 'success' });
    } catch (err: any) {
      setMessage({ text: err.message, type: 'error' });
    }
  };

  // ---- Reindex ----
  const handleReindex = async (sourceId: string) => {
    if (!tenantId) return;
    try {
      const result = await api.reindexKBSource(tenantId, sourceId);
      setMessage({ text: `Reindexed — ${result.chunk_count} chunks`, type: 'success' });
      loadSources();
    } catch (err: any) {
      setMessage({ text: err.message, type: 'error' });
    }
  };

  // ---- Search ----
  const handleSearch = async () => {
    if (!searchQuery.trim() || !tenantId) return;
    setSearching(true);
    setSearchResults([]);
    try {
      const { results } = await api.searchKB(tenantId, searchQuery);
      setSearchResults(results);
      if (results.length === 0) {
        setMessage({ text: 'No results found. Try different keywords.', type: 'error' });
      }
    } catch (err: any) {
      setMessage({ text: err.message, type: 'error' });
    }
    setSearching(false);
  };

  // ---- View Chunks ----
  const handleToggleChunks = async (sourceId: string) => {
    if (expandedSource === sourceId) {
      setExpandedSource(null);
      setSourceChunks(null);
      return;
    }
    if (!tenantId) return;
    try {
      const data = await api.getKBSourceDetail(tenantId, sourceId);
      setSourceChunks(data);
      setExpandedSource(sourceId);
    } catch {}
  };

  const totalChunks = sources.reduce((sum: number, s: any) => sum + (s.document_count || 0), 0);

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="h-4 bg-gray-200 rounded w-96"></div>
        </div>
      </div>
    );
  }

  if (!tenantId) {
    return (
      <div className="p-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
          <AlertCircle className="h-10 w-10 text-yellow-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-yellow-800">No Tenants Found</h2>
          <p className="text-sm text-yellow-700 mt-1">Create a tenant first before managing the knowledge base.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Knowledge Base</h1>
        <p className="text-gray-500 mt-1">
          Upload text files or paste content to give the chatbot domain-specific knowledge.
          The chatbot automatically searches this content when answering questions.
        </p>
      </div>

      {/* Tenant selector (if multiple) */}
      {tenants.length > 1 && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Tenant</label>
          <select
            value={tenantId}
            onChange={e => setTenantId(e.target.value)}
            className="w-64 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
          >
            {tenants.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Status message */}
      {message && (
        <div className={`mb-6 flex items-center gap-2 px-4 py-3 rounded-lg text-sm ${
          message.type === 'error'
            ? 'bg-red-50 text-red-700 border border-red-200'
            : 'bg-green-50 text-green-700 border border-green-200'
        }`}>
          {message.type === 'error' ? <AlertCircle className="h-4 w-4 flex-shrink-0" /> : <CheckCircle2 className="h-4 w-4 flex-shrink-0" />}
          {message.text}
        </div>
      )}

      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Sources</p>
          <p className="text-2xl font-bold text-gray-900">{sources.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Chunks</p>
          <p className="text-2xl font-bold text-primary">{totalChunks}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Status</p>
          <p className="text-2xl font-bold text-green-600">
            {sources.every(s => s.status === 'indexed') && sources.length > 0 ? 'Ready' : sources.length === 0 ? 'Empty' : 'Processing'}
          </p>
        </div>
      </div>

      {/* Action tabs */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="border-b border-gray-200 px-4 pt-4">
          <div className="flex gap-1">
            {([
              { key: 'sources', label: 'Sources', icon: Database },
              { key: 'upload', label: 'Upload File', icon: Upload },
              { key: 'paste', label: 'Paste Text', icon: FileText },
              { key: 'search', label: 'Test Search', icon: Search },
            ] as const).map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-primary text-primary bg-primary/5'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {/* ====== Sources List ====== */}
          {activeTab === 'sources' && (
            sources.length === 0 ? (
              <div className="text-center py-12">
                <Database className="h-16 w-16 text-gray-200 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-1">No knowledge sources yet</h3>
                <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto">
                  Upload a text file or paste content to get started. The chatbot will use this information when answering questions.
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => setActiveTab('upload')}
                    className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90"
                  >
                    <Upload className="h-4 w-4" /> Upload File
                  </button>
                  <button
                    onClick={() => setActiveTab('paste')}
                    className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50"
                  >
                    <FileText className="h-4 w-4" /> Paste Text
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-700">{sources.length} source{sources.length !== 1 ? 's' : ''}</h3>
                  <button
                    onClick={() => setActiveTab('upload')}
                    className="inline-flex items-center gap-1.5 text-sm text-primary font-medium hover:text-primary/80"
                  >
                    <Plus className="h-4 w-4" /> Add Source
                  </button>
                </div>

                {sources.map((s: any) => (
                  <div key={s.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 bg-gray-50/50">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          s.status === 'indexed' ? 'bg-green-500' : s.status === 'failed' ? 'bg-red-500' : 'bg-yellow-500'
                        }`} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{s.name}</p>
                          <p className="text-xs text-gray-500">
                            {s.document_count} chunk{s.document_count !== 1 ? 's' : ''}
                            <span className="mx-1.5 text-gray-300">|</span>
                            {s.type}
                            {s.last_indexed_at && (
                              <>
                                <span className="mx-1.5 text-gray-300">|</span>
                                <Clock className="inline h-3 w-3 -mt-0.5" /> {new Date(s.last_indexed_at).toLocaleDateString()}
                              </>
                            )}
                            {s.error_message && (
                              <>
                                <span className="mx-1.5 text-gray-300">|</span>
                                <span className="text-red-500">{s.error_message}</span>
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-4 flex-shrink-0">
                        <button
                          onClick={() => handleToggleChunks(s.id)}
                          className="p-1.5 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-md transition-colors"
                          title="View chunks"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleReindex(s.id)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                          title="Reindex"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(s.id, s.name)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Expanded chunk preview */}
                    {expandedSource === s.id && sourceChunks && (
                      <div className="border-t border-gray-200 bg-white p-4 max-h-80 overflow-y-auto">
                        <p className="text-xs text-gray-500 mb-3">
                          Showing {sourceChunks.chunks?.length} of {sourceChunks.totalChunks} chunks
                        </p>
                        <div className="space-y-2">
                          {sourceChunks.chunks?.map((chunk: any, i: number) => (
                            <div key={chunk.id || i} className="text-xs text-gray-600 bg-gray-50 rounded p-3 font-mono leading-relaxed">
                              <span className="text-gray-400 font-medium">#{chunk.chunk_index}</span>{' '}
                              {chunk.content.slice(0, 300)}{chunk.content.length > 300 ? '...' : ''}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )
          )}

          {/* ====== Upload File ====== */}
          {activeTab === 'upload' && (
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Upload a <strong>.txt</strong>, <strong>.md</strong>, <strong>.csv</strong>, <strong>.pdf</strong>, or <strong>.docx</strong> file (max 10 MB).
                The file will be parsed, chunked, and indexed automatically.
              </p>
              <div
                className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
                  uploading ? 'border-gray-200 bg-gray-50' : 'border-gray-300 hover:border-primary/40 hover:bg-primary/5 cursor-pointer'
                }`}
                onClick={() => !uploading && fileInputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  const file = e.dataTransfer.files[0];
                  if (file && fileInputRef.current) {
                    const dt = new DataTransfer();
                    dt.items.add(file);
                    fileInputRef.current.files = dt.files;
                    fileInputRef.current.dispatchEvent(new Event('change', { bubbles: true }));
                  }
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.md,.csv,.pdf,.docx"
                  onChange={handleFileUpload}
                  disabled={uploading}
                  className="hidden"
                />
                <Upload className={`h-12 w-12 mx-auto mb-4 ${uploading ? 'text-gray-300 animate-pulse' : 'text-gray-400'}`} />
                <p className="text-sm font-medium text-gray-700 mb-1">
                  {uploading ? 'Uploading and processing...' : 'Click to choose a file or drag and drop'}
                </p>
                <p className="text-xs text-gray-400">
                  .txt, .md, .csv, .pdf, .docx — max 10 MB
                </p>
              </div>
            </div>
          )}

          {/* ====== Paste Text ====== */}
          {activeTab === 'paste' && (
            <div className="max-w-3xl">
              <p className="text-sm text-gray-600 mb-4">
                Paste text content directly. Great for FAQs, product descriptions, policies, or any knowledge you want the chatbot to reference.
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source Name</label>
                  <input
                    type="text"
                    value={textName}
                    onChange={e => setTextName(e.target.value)}
                    placeholder="e.g. Product FAQ, Company Policies, Return Policy"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                  <textarea
                    value={textContent}
                    onChange={e => setTextContent(e.target.value)}
                    placeholder="Paste your knowledge base content here...&#10;&#10;Tip: Use clear paragraphs. Each paragraph becomes a searchable chunk."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    rows={16}
                  />
                  <p className="text-xs text-gray-400 mt-1">{textContent.length.toLocaleString()} characters</p>
                </div>
                <button
                  onClick={handleTextSubmit}
                  disabled={uploading || !textName.trim() || !textContent.trim()}
                  className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-primary/90 transition-colors"
                >
                  {uploading ? (
                    <><RefreshCw className="h-4 w-4 animate-spin" /> Processing...</>
                  ) : (
                    <><Plus className="h-4 w-4" /> Add to Knowledge Base</>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* ====== Test Search ====== */}
          {activeTab === 'search' && (
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Test how the chatbot searches the knowledge base. Type a question like a user would ask.
              </p>
              <div className="flex gap-2 mb-6">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  placeholder="e.g. What is the return policy? How much does..."
                  className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
                <button
                  onClick={handleSearch}
                  disabled={searching || !searchQuery.trim()}
                  className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-primary/90"
                >
                  {searching ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  Search
                </button>
              </div>

              {searchResults.length > 0 && (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700">{searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found</p>
                  {searchResults.map((r: any, i: number) => (
                    <div key={i} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                          {r.sourceTitle}
                        </span>
                        <span className="text-xs text-gray-400">score: {r.score?.toFixed(4)}</span>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                        {r.content.slice(0, 500)}{r.content.length > 500 ? '...' : ''}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {searchResults.length === 0 && searchQuery && !searching && (
                <div className="text-center py-8">
                  <Search className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No results found for "{searchQuery}"</p>
                  <p className="text-xs text-gray-400 mt-1">Make sure you've added content to the knowledge base first.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
